import {PhoneHQAvatar, PhoneHQCheckbox, PhoneHQGlyph, PhoneHQLoader} from 'ch3-ui-lib';
import {GoogleUser, GoogleUserImportStatus} from "../../../store/features/integrations/integrationsService";

type ExistingUserStatus = 'ALREADY_EXISTS' | 'ALREADY_SYNCED';
type DisplayStatus = GoogleUserImportStatus | ExistingUserStatus | undefined;

interface UserRowProps {
    user: GoogleUser;
    isSelected: boolean;
    handleSelect: (user: GoogleUser) => void;
    userStatus?: GoogleUserImportStatus;
    userMessage?: string | null;
    isPolling: boolean;
    showImportStatus: boolean;
    userExists: ExistingUserStatus | false;
}

const statusContent = (status: DisplayStatus, isPolling: boolean) => {
    switch (status) {
        case 'FAILED':
            return (
                <div className='flex text-danger-ink items-center'>
                    <PhoneHQGlyph name='error' size={16} className='text-red-500'/>
                    Failed
                </div>
            );
        case 'PARTIAL':
            return (
                <div className='flex text-warning-ink items-center'>
                    <PhoneHQGlyph name='error' size={16} className='text-warning-ink'/>
                    Partial
                </div>
            );
        case 'SUCCESS':
            return (
                <div className='flex text-success-ink items-center'>
                    <PhoneHQGlyph name='check' size={16} className='text-green-300'/>
                    Success
                </div>
            );
        case 'UPDATED':
            return (
                <div className='flex text-success-ink items-center'>
                    <PhoneHQGlyph name='check' size={16} className='text-green-300'/>
                    Updated
                </div>
            );
        case 'SKIPPED':
            return <div className='text-sm text-ink-secondary'>Skipped</div>;
        case 'ALREADY_EXISTS':
            return (
                <div className='flex text-ink items-center text-sm'>
                    <PhoneHQGlyph name='check' size={12} className='text-white bg-black mx-2 w-4 h-4 justify-center'/>
                    Already Exists
                </div>
            );
        case 'ALREADY_SYNCED':
            return (
                <div className='flex text-ink items-center text-sm'>
                    <PhoneHQGlyph name='check' size={12} className='text-white bg-black mx-2 w-4 h-4 justify-center'/>
                    Already Synced
                </div>
            );
        case 'WAITING':
            return isPolling
                ? <PhoneHQLoader className='!h-[36px] w-full'/>
                : <div className='text-sm text-ink-secondary'>Waiting</div>;
        case undefined:
            return isPolling
                ? <PhoneHQLoader className='!h-[36px] w-full'/>
                : <div className='text-sm text-ink-secondary'>Status unavailable</div>;
    }
};

export const UserRow = ({
    user,
    userExists,
    isSelected,
    handleSelect,
    userStatus,
    userMessage,
    isPolling,
    showImportStatus,
}: UserRowProps) => {
    const displayedStatus = userExists || userStatus;

    return (
        <td className={`flex items-center justify-between border-b border-line`}>
            <div className='flex items-center gap-3 p-3'>
                {!showImportStatus && !userExists && (
                    <PhoneHQCheckbox
                        onCheckedChange={() => handleSelect(user)}
                        checked={isSelected}
                        value={user.email}
                    />
                )}
                <PhoneHQAvatar src={user.avatarUrl ?? undefined} size="40px"/>
                <div>
                    <div className='font-semibold'>{`${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unnamed User'}</div>
                    <div>{user.email}</div>
                </div>
            </div>

            {(showImportStatus || userExists) && (
                <div className='flex max-w-[320px] flex-col items-end px-7'>
                    {statusContent(displayedStatus, isPolling)}
                    {userMessage && (userStatus === 'FAILED' || userStatus === 'PARTIAL') && (
                        <span className='mt-1 text-right text-xs text-ink-secondary' title={userMessage}>
                            {userMessage}
                        </span>
                    )}
                </div>
            )}
        </td>
    );
};
